import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import {
  Upload, FileText, CheckCircle, X, Layers, Smartphone, CreditCard,
  FlaskConical, Loader2, ChevronRight, Cpu, AlertTriangle, ShieldCheck,
  Download, Info, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGENT_URL = "http://localhost:7979";

const STEPS = ["Documentos", "Certificado", "Assinar Lote", "Concluído"];

function OtpTimer() {
  const [remaining, setRemaining] = useState(30 - (Math.floor(Date.now() / 1000) % 30));
  useEffect(() => {
    const i = setInterval(() => setRemaining(30 - (Math.floor(Date.now() / 1000) % 30)), 1000);
    return () => clearInterval(i);
  }, []);
  const urgent = remaining <= 8;
  return (
    <span className={`text-xs font-medium ${urgent ? "text-red-600" : "text-gray-500"}`}>
      <Clock size={12} className="inline mr-1" />
      {urgent ? "Renove o OTP!" : `Renova em ${remaining}s`}
    </span>
  );
}

export default function BatchSignPage() {
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [batchItems, setBatchItems] = useState([]); // {document_id, filename, hash, sign_type, status, error?}
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef();

  // Cert mode
  const [certMode, setCertMode] = useState("birdid");
  const [agentStatus, setAgentStatus] = useState("checking");
  const [certificates, setCertificates] = useState([]);
  const [selectedCert, setSelectedCert] = useState(null);
  const [otp, setOtp] = useState("");
  const [pin, setPin] = useState("");

  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [visibleSignature, setVisibleSignature] = useState(true);

  const handleFileSelect = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const selected = Array.from(e.dataTransfer?.files || e.target.files || []);
    if (!selected.length) return;
    setFiles((prev) => [...prev, ...selected]);
  }, []);

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleBatchUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("sign_type", "auto");
      const res = await axios.post(`${API}/sign/batch/upload`, fd);
      setBatchItems(res.data.items.map((it) => ({ ...it, status: "pending" })));
      setStep(1);
    } catch (e) {
      setError("Erro ao enviar arquivos: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const checkAgent = useCallback(async () => {
    setAgentStatus("checking");
    try {
      const res = await fetch(`${AGENT_URL}/status`, { signal: AbortSignal.timeout(3000) });
      const data = await res.json();
      setAgentStatus("online");
      const cr = await fetch(`${AGENT_URL}/certificates`, { signal: AbortSignal.timeout(5000) });
      const cd = await cr.json();
      const certs = cd.certificates || [];
      setCertificates(certs);
      if (certs.length) setSelectedCert(certs[0].alias);
      if (data.birdid_detected || certs.some((c) => c.is_birdid)) setCertMode("birdid");
    } catch {
      setAgentStatus("offline");
      setCertificates([]);
    }
  }, []);

  useEffect(() => { if (step === 1) checkAgent(); }, [step, checkAgent]);

  const handleBatchSign = async () => {
    setSigning(true);
    setError(null);

    try {
      // DEMO MODE: call /api/sign/demo for each doc
      if (certMode === "demo") {
        const updated = [...batchItems];
        for (let i = 0; i < updated.length; i++) {
          updated[i].status = "signing";
          setBatchItems([...updated]);
          try {
            const r = await axios.post(`${API}/sign/demo/${updated[i].document_id}`);
            updated[i].status = "signed";
            updated[i].signer_cn = r.data.signer_cn;
            updated[i].signed_at = r.data.signed_at;
          } catch (e) {
            updated[i].status = "failed";
            updated[i].error = e.response?.data?.detail || e.message;
          }
          setBatchItems([...updated]);
        }
        setResult({
          succeeded: updated.filter((x) => x.status === "signed").length,
          failed: updated.filter((x) => x.status === "failed").length,
          total: updated.length,
        });
        setStep(3);
        return;
      }

      // BIRDID / A3: prepare hashes per-doc (PAdES precisa do cert), depois envia TODOS os
      // hashes em UMA unica chamada /sign-batch para que o agente autentique 1 vez (1 OTP/PIN)
      // e assine todos os documentos na MESMA sessao PKCS#11/CNG autenticada.
      const certObj = certificates.find((c) => c.alias === selectedCert);
      if (!certObj) throw new Error("Selecione um certificado válido.");

      let certPem = certObj.cert_pem;
      const updated = [...batchItems];

      // Etapa 1: preparar hashes (todos antes do agente, para que o OTP nao expire durante o prepare)
      const prepHashes = [];
      const prepIndices = [];
      for (let i = 0; i < updated.length; i++) {
        updated[i].status = "signing";
        setBatchItems([...updated]);
        try {
          const prepRes = await axios.post(`${API}/sign/prepare/${updated[i].document_id}`, { 
            certificate_pem: certPem,
            visible_signature: visibleSignature
          });
          prepHashes.push(prepRes.data.hash);
          prepIndices.push(i);
        } catch (e) {
          updated[i].status = "failed";
          updated[i].error = "Erro ao preparar hash: " + (e.response?.data?.detail || e.message);
        }
      }
      setBatchItems([...updated]);

      if (prepHashes.length === 0) {
        throw new Error("Nenhum documento pôde ser preparado para assinatura.");
      }

      // Etapa 2: enviar TODOS os hashes em uma unica chamada batch (autenticacao unica)
      const batchPayload = {
        hashes: prepHashes,
        certificate_alias: selectedCert,
        pin: certMode === "birdid" ? otp : pin,
        mode: certMode,
      };

      let agentData;
      try {
        const agentRes = await axios.post(`${AGENT_URL}/sign-batch`, batchPayload, { timeout: 180000 });
        agentData = agentRes.data;
      } catch (err) {
        if (err.response && err.response.data) {
          agentData = err.response.data;
        } else {
          throw err;
        }
      }
      if (agentData.error) {
        for (const i of prepIndices) {
          updated[i].status = "failed";
          updated[i].error = agentData.error;
        }
        setBatchItems([...updated]);
        throw new Error(agentData.error);
      }
      if (!agentData.signatures || agentData.signatures.length !== prepHashes.length) {
        throw new Error("Número de assinaturas retornadas pelo agente não corresponde ao lote");
      }
      if (!certPem) certPem = agentData.certificate_pem;

      const signedItems = [];
      for (let k = 0; k < prepIndices.length; k++) {
        const i = prepIndices[k];
        updated[i].status = "signed-agent";
        signedItems.push({
          document_id: updated[i].document_id,
          signature_base64: agentData.signatures[k],
          sign_type: updated[i].sign_type,
        });
      }
      setBatchItems([...updated]);

      // Now finalize all successful ones in one batch call
      if (signedItems.length > 0 && certPem) {
        const r = await axios.post(`${API}/sign/batch/complete`, {
          certificate_pem: certPem,
          items: signedItems,
        });
        for (const ok of r.data.succeeded) {
          const idx = updated.findIndex((u) => u.document_id === ok.document_id);
          if (idx >= 0) {
            updated[idx].status = "signed";
            updated[idx].signer_cn = ok.signer_cn;
            updated[idx].signed_at = ok.signed_at;
          }
        }
        for (const fail of r.data.failed) {
          const idx = updated.findIndex((u) => u.document_id === fail.document_id);
          if (idx >= 0) {
            updated[idx].status = "failed";
            updated[idx].error = fail.error;
          }
        }
        setBatchItems([...updated]);
      }

      setResult({
        succeeded: updated.filter((x) => x.status === "signed").length,
        failed: updated.filter((x) => x.status === "failed").length,
        total: updated.length,
      });
      setStep(3);
    } catch (e) {
      let msg = e.message;
      if (msg.includes("OTP") || msg.includes("PIN") || msg.includes("CKR_PIN")) {
        msg = "OTP/PIN inválido ou expirado. Renove e tente novamente.";
      }
      setError(msg);
    } finally {
      setSigning(false);
    }
  };

  const downloadAll = async () => {
    for (const item of batchItems.filter((x) => x.status === "signed")) {
      try {
        const r = await axios.get(`${API}/documents/${item.document_id}/download/signed`);
        const { filename, content_base64 } = r.data;
        const b = atob(content_base64);
        const arr = new Uint8Array(b.length);
        for (let i = 0; i < b.length; i++) arr[i] = b.charCodeAt(i);
        const url = URL.createObjectURL(new Blob([arr]));
        const a = document.createElement("a");
        a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        await new Promise((r2) => setTimeout(r2, 250));
      } catch { /* ignore individual failures */ }
    }
  };

  const canProceed = certMode === "demo" ||
    (agentStatus === "online" && (certMode === "a3token" || (certMode === "birdid" && (otp.length === 0 || otp.length === 6))));

  return (
    <div className="p-8 max-w-3xl mx-auto" data-testid="batch-sign-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-1">
          <Layers size={22} className="text-[#163A24]" />
          <h1 className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Assinatura em Lote
          </h1>
        </div>
        <p className="text-gray-500 text-sm">Assine múltiplos documentos com uma única autenticação OTP/PIN</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center mb-8">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${i < step ? "step-done" : i === step ? "step-active" : "step-pending"}`}>
                {i < step ? <CheckCircle size={14} /> : i + 1}
              </div>
              <span className={`text-xs font-medium ${i === step ? "text-[#163A24]" : "text-gray-400"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-3 ${i < step ? "bg-[#8FBC8F]" : "bg-gray-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700" data-testid="batch-error">
          <AlertTriangle size={16} className="mt-0.5" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400">✕</button>
        </div>
      )}

      {/* STEP 0: file selection */}
      {step === 0 && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 space-y-5" data-testid="batch-step-files">
          <div
            onDrop={handleFileSelect}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${dragging ? "border-[#163A24] bg-[#163A24]/5" : "border-gray-200 hover:border-[#163A24]/40 hover:bg-gray-50"}`}
            data-testid="batch-dropzone"
          >
            <input ref={fileInputRef} type="file" multiple className="hidden" accept=".pdf,.xml,.txt,.docx" onChange={handleFileSelect} data-testid="batch-file-input" />
            <Upload size={36} className="text-gray-300 mb-3" />
            <p className="font-medium text-gray-600">Arraste vários arquivos aqui</p>
            <p className="text-xs text-gray-400 mt-1">ou clique para selecionar (até 50 arquivos)</p>
          </div>

          {files.length > 0 && (
            <div className="space-y-2" data-testid="batch-file-list">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{files.length} arquivo(s) selecionado(s)</p>
              {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className="flex items-center gap-3 p-3 bg-[#FAF9F6] rounded-lg">
                  <FileText size={16} className="text-[#163A24] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{f.name}</p>
                    <p className="text-[11px] text-gray-400">{(f.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500" data-testid={`batch-remove-${i}`}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            data-testid="btn-batch-upload"
            onClick={handleBatchUpload}
            disabled={!files.length || uploading}
            className="w-full flex items-center justify-center gap-2 bg-[#163A24] text-white py-3 rounded-md font-semibold text-sm hover:bg-[#205234] disabled:opacity-40 transition-all duration-200"
          >
            {uploading ? <><Loader2 size={16} className="animate-spin" /> Enviando...</> : <>Prosseguir ({files.length}) <ChevronRight size={16} /></>}
          </button>
        </div>
      )}

      {/* STEP 1: certificate */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 space-y-5" data-testid="batch-step-cert">
          <div>
            <h3 className="font-semibold text-gray-800" style={{ fontFamily: "Outfit, sans-serif" }}>Tipo de Certificado</h3>
            <p className="text-xs text-gray-400 mt-0.5">{batchItems.length} documento(s) prontos para assinatura</p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              { key: "birdid", icon: Smartphone, label: "BirdID Desktop", desc: "Um OTP para todos" },
              { key: "a3token", icon: CreditCard, label: "Token A3", desc: "Um PIN para todos" },
              { key: "demo", icon: FlaskConical, label: "Demonstração", desc: "Sem validade" },
            ].map(({ key, icon: Icon, label, desc }) => (
              <button
                key={key}
                data-testid={`batch-cert-mode-${key}`}
                onClick={() => setCertMode(key)}
                className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${certMode === key ? "border-[#163A24] bg-[#163A24]/5" : "border-gray-200 hover:border-[#163A24]/40"}`}
              >
                <Icon size={22} className={certMode === key ? "text-[#163A24] mb-2" : "text-gray-400 mb-2"} />
                <p className={`text-xs font-semibold ${certMode === key ? "text-[#163A24]" : "text-gray-600"}`}>{label}</p>
                <p className="text-[10px] text-gray-400 mt-0.5 text-center">{desc}</p>
              </button>
            ))}
          </div>

          {certMode !== "demo" && (
            <div className={`flex items-center gap-3 p-4 rounded-lg border ${agentStatus === "online" ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"}`}>
              <Cpu size={18} className={agentStatus === "online" ? "text-green-600" : "text-yellow-600"} />
              <p className={`text-sm font-semibold ${agentStatus === "online" ? "text-green-700" : "text-yellow-700"}`}>
                {agentStatus === "online" ? "Agente conectado" : agentStatus === "checking" ? "Verificando..." : "Agente offline"}
              </p>
            </div>
          )}

          {certMode === "birdid" && agentStatus === "online" && (
            <div className="space-y-3">
              {certificates.length > 1 && (
                <select value={selectedCert || ""} onChange={(e) => setSelectedCert(e.target.value)}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="batch-cert-select">
                  {certificates.map((c) => <option key={c.alias} value={c.alias}>{c.cn || c.label}</option>)}
                </select>
              )}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Código OTP do BirdID</label>
                  <OtpTimer />
                </div>
                <input
                  data-testid="batch-otp-input"
                  type="text" inputMode="numeric" maxLength={6} value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="w-full text-center text-2xl font-bold tracking-widest border-2 border-gray-200 rounded-xl py-3 focus:outline-none focus:border-[#163A24]"
                />
                <p className="text-xs text-gray-400 mt-2 text-center">
                  <Info size={11} className="inline mr-1" />
                  O mesmo OTP será usado para todos os {batchItems.length} documento(s)
                </p>
                <p className="text-center text-[10px] text-gray-500 mt-2 bg-gray-50 p-2 rounded-lg border border-gray-100">
                  <strong>Dica:</strong> Se o seu Agente Local já abre a janela do BirdID automaticamente, você pode deixar este campo em branco.
                </p>
              </div>
            </div>
          )}

          {certMode === "a3token" && agentStatus === "online" && (
            <input type="password" value={pin} onChange={(e) => setPin(e.target.value)}
              placeholder="PIN do Token A3" autoComplete="off"
              className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#163A24]"
              data-testid="batch-pin-input" />
          )}

          {certMode === "demo" && (
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Info size={16} className="text-blue-500 mt-0.5" />
              <p className="text-xs text-blue-700">Modo Demonstração — todos os documentos serão assinados com certificado de teste, sem validade jurídica.</p>
            </div>
          )}

          {/* VISIBLE SIGNATURE OPTION */}
          {batchItems.some(it => it.sign_type === "PAdES") && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visibleSignature}
                  onChange={(e) => setVisibleSignature(e.target.checked)}
                  className="w-4 h-4 text-[#163A24] rounded border-gray-300 focus:ring-[#163A24]"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Adicionar assinatura visual no rodapé (PDFs)</p>
                  <p className="text-[11px] text-gray-500">Inclui dados de autenticação no final do documento</p>
                </div>
              </label>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => setStep(0)} className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-md text-sm font-medium hover:bg-gray-50">Voltar</button>
            <button
              onClick={() => setStep(2)} disabled={!canProceed}
              data-testid="btn-batch-proceed-sign"
              className="flex-1 flex items-center justify-center gap-2 bg-[#163A24] text-white py-2.5 rounded-md font-semibold text-sm hover:bg-[#205234] disabled:opacity-40">
              Prosseguir <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: confirm + sign */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 space-y-5" data-testid="batch-step-sign">
          <div>
            <h3 className="font-semibold text-gray-800" style={{ fontFamily: "Outfit, sans-serif" }}>Confirmar Assinatura em Lote</h3>
            <p className="text-xs text-gray-400 mt-0.5">{batchItems.length} documento(s) · {certMode === "birdid" ? "BirdID OTP" : certMode === "a3token" ? "Token A3" : "Demo"}</p>
          </div>

          <div className="bg-[#FAF9F6] rounded-lg p-4 max-h-72 overflow-y-auto space-y-2" data-testid="batch-doc-list">
            {batchItems.map((it) => (
              <div key={it.document_id} className="flex items-center gap-3 p-2 bg-white rounded border border-gray-100">
                <FileText size={14} className="text-[#163A24] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{it.filename}</p>
                  <p className="text-[10px] text-gray-400">{it.sign_type} · hash: {it.hash.slice(0, 16)}…</p>
                </div>
                {it.status === "pending" && <span className="text-[10px] text-gray-400">Aguardando</span>}
                {it.status === "signing" && <Loader2 size={14} className="text-[#163A24] animate-spin" />}
                {it.status === "signed-agent" && <Loader2 size={14} className="text-blue-500 animate-spin" />}
                {it.status === "signed" && <CheckCircle size={14} className="text-green-600" />}
                {it.status === "failed" && <X size={14} className="text-red-500" />}
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <button onClick={() => setStep(1)} disabled={signing} className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-40">Voltar</button>
            <button
              onClick={handleBatchSign} disabled={signing}
              data-testid="btn-batch-sign-all"
              className="flex-1 flex items-center justify-center gap-2 bg-[#163A24] text-white py-3 rounded-md font-bold text-sm hover:bg-[#205234] disabled:opacity-60">
              {signing ? <><Loader2 size={16} className="animate-spin" /> Assinando lote...</> : <><Layers size={16} /> Assinar {batchItems.length} documento(s)</>}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: result */}
      {step === 3 && result && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-8 space-y-5" data-testid="batch-step-done">
          <div className="text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${result.failed === 0 ? "bg-green-50" : "bg-yellow-50"}`}>
              {result.failed === 0 ? <ShieldCheck size={32} className="text-green-600" /> : <AlertTriangle size={32} className="text-yellow-600" />}
            </div>
            <h3 className="text-xl font-bold text-[#1A1A1A] mt-4" style={{ fontFamily: "Outfit, sans-serif" }}>
              {result.failed === 0 ? "Lote assinado!" : "Lote concluído com erros"}
            </h3>
            <p className="text-gray-500 text-sm mt-1" data-testid="batch-result-summary">
              {result.succeeded} de {result.total} documento(s) assinado(s) com sucesso
              {result.failed > 0 && ` · ${result.failed} falha(s)`}
            </p>
          </div>

          <div className="bg-[#FAF9F6] rounded-lg p-4 max-h-60 overflow-y-auto space-y-2">
            {batchItems.map((it) => (
              <div key={it.document_id} className="flex items-center gap-3 text-sm">
                {it.status === "signed" ? <CheckCircle size={14} className="text-green-600" /> : <X size={14} className="text-red-500" />}
                <span className="flex-1 truncate text-gray-800">{it.filename}</span>
                {it.error && <span className="text-[10px] text-red-500 truncate max-w-[200px]" title={it.error}>{it.error}</span>}
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            {result.succeeded > 0 && (
              <button onClick={downloadAll}
                data-testid="btn-batch-download-all"
                className="flex-1 flex items-center justify-center gap-2 bg-[#163A24] text-white py-3 rounded-md font-semibold text-sm hover:bg-[#205234]">
                <Download size={16} /> Baixar todos ({result.succeeded})
              </button>
            )}
            <button onClick={() => { setStep(0); setFiles([]); setBatchItems([]); setResult(null); setOtp(""); setPin(""); }}
              className="px-4 py-3 border border-gray-200 text-gray-600 rounded-md text-sm font-medium hover:bg-gray-50">
              Novo lote
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
